import React, { useState, useEffect } from 'react';
import { AIMessagesBanner } from 'components/AI/AIBanner/AIMessagesBanner/AIMessagesBanner';
import { Stack } from '@mui/material';
import { useAiEnabled } from 'hooks/ai-firm-settings-hooks';
import { isBannerDismissed, dismissBanner } from 'utils/bannerStorageUtils';

// Storage key for this banner's dismissed state.
const BANNER_TYPE = 'message';

export interface AIFlaggedBannerProps {
  /**
   * Whether this banner should be dismissible (defaults to false for backward compatibility)
   */
  dismissible?: boolean;
}

/**
 * AIFlaggedBanner renders the AI messages banner, gated on AI being enabled
 * for the firm. (The file-type banner was removed in LISD-2498; the Files page
 * now shows the persistent AIFileReviewButton instead.)
 *
 * @param props - Component props
 * @returns React component or null if AI is disabled or the banner is dismissed
 */
export const AIFlaggedBanner: React.FC<AIFlaggedBannerProps> = ({
  dismissible = false,
}) => {
  const { isAiEnabled } = useAiEnabled();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if the banner has been dismissed on mount
  useEffect(() => {
    if (dismissible) {
      setIsDismissed(isBannerDismissed(BANNER_TYPE));
    }
  }, [dismissible]);

  // Handle banner dismissal
  const handleDismiss = () => {
    dismissBanner(BANNER_TYPE);
    setIsDismissed(true);
  };

  // Return null if the feature flag is disabled
  if (!isAiEnabled) {
    return null;
  }

  // Return null if banner is dismissed
  if (dismissible && isDismissed) {
    return null;
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={4}
      sx={{ padding: '0 0 1.6rem 0' }}
    >
      <Stack sx={{ flex: 1 }}>
        <AIMessagesBanner
          dismissible={dismissible}
          onDismiss={dismissible ? handleDismiss : undefined}
        />
      </Stack>
    </Stack>
  );
};

export default AIFlaggedBanner;
